import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { examAssignmentsAPI } from "@/services/api";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";

const EditAssignmentModal = ({ isOpen, onClose, assignmentId, onSuccess }) => {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    dateOfExam: "",
    graceTime: 5,
    status: "pending",
    reviewStatus: "pending",
    sendUpdateEmail: false,
  });
  const { toast } = useToast();

  // Get assignment details
  const { data: assignment, isLoading } = useQuery({
    queryKey: ["exam-assignment", assignmentId],
    queryFn: async () => {
      try {
        const assignmentRes = await examAssignmentsAPI.getOne(assignmentId);
        if (assignmentRes.data?.status === 'completed') {
          return {
            ...assignmentRes,
            data: {
              ...assignmentRes.data,
              reviewStatus: assignmentRes.data.reviewStatus || 'pending',
              graceTime: assignmentRes.data.graceTime || 5
            }
          };
        }
        return assignmentRes;
      } catch (error) {
        console.error("Error fetching assignment:", error);
        throw error;
      }
    },
    enabled: isOpen && !!assignmentId,
  });

  // Update assignment mutation
  const updateMutation = useMutation({
    mutationFn: async (updateData) => {
      return await examAssignmentsAPI.update(assignmentId, updateData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["exam-assignment", assignmentId]);
      toast({
        title: "Success",
        description: "Assignment updated successfully",
      });
      onSuccess?.();
      onClose();
    },
    onError: (error) => {
      console.error("Update error:", {
        error: error.message,
        response: error.response?.data
      });
      toast({
        title: "Error",
        description: error.response?.data?.message || 
                   error.message || 
                   "Failed to update assignment",
        variant: "destructive",
      });
    }
  });

  useEffect(() => {
    if (assignment?.data) {
      const assignmentData = assignment.data;
      let formattedDate = "";

      if (assignmentData.dateOfExam) {
        try {
          const storedDate = new Date(assignmentData.dateOfExam);
          if (!isNaN(storedDate.getTime())) {
            const timezoneOffset = storedDate.getTimezoneOffset() * 60000;
            const localDate = new Date(storedDate.getTime() - timezoneOffset);
            formattedDate = localDate.toISOString().slice(0, 16);
          }
        } catch (error) {
          console.error("Date parsing error:", error);
        }
      }

      setFormData({
        dateOfExam: formattedDate,
        graceTime: assignmentData.graceTime || 5,
        status: assignmentData.status || "pending",
        reviewStatus: assignmentData.reviewStatus || "pending",
        sendUpdateEmail: false,
      });
    }
  }, [assignment, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const updateData = {
      status: formData.status,
      graceTime: Number(formData.graceTime),
      sendUpdateEmail: formData.sendUpdateEmail,
      ...(formData.status === 'completed' && { reviewStatus: formData.reviewStatus })
    };

    if (formData.dateOfExam) {
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const examDateTime = new Date(formData.dateOfExam);
      updateData.dateOfExam = examDateTime.toISOString();
      updateData.userTimezone = userTimezone;
    }

    updateMutation.mutate(updateData);
  };

  const handleClearDateTime = () => {
    setFormData(prev => ({ ...prev, dateOfExam: "" }));
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
      ...(field === 'status' && value !== 'completed' ? { reviewStatus: 'pending' } : {})
    }));
  };

  const getMinDateTime = () => {
    const now = new Date();
    return format(now, "yyyy-MM-dd'T'HH:mm");
  };

  if (isLoading) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent>
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary"></div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit Exam Assignment</DialogTitle>
        </DialogHeader>

        {assignment?.data && (
          <div className="mb-4 p-4 bg-gray-50 rounded-lg">
            <p><strong>Candidate:</strong> {assignment.data.candidateId?.name}</p>
            <p><strong>Email:</strong> {assignment.data.candidateId?.email}</p>
            <p><strong>Exam:</strong> {assignment.data.examId?.name}</p>
            <p><strong>Duration:</strong> {assignment.data.examId?.totalTime} minutes</p>
            {assignment.data.graceTime > 0 && (
              <p><strong>Current Grace Time:</strong> {assignment.data.graceTime} minutes</p>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <Label htmlFor="dateOfExam">Exam Date & Time</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleClearDateTime}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Clear
              </Button>
            </div>
            <Input
              id="dateOfExam"
              type="datetime-local"
              value={formData.dateOfExam}
              min={getMinDateTime()}
              onChange={(e) => handleChange("dateOfExam", e.target.value)}
              disabled={assignment?.data?.status === 'completed'}
            />
            <p className="text-xs text-gray-500">
              Current: {assignment?.data?.dateOfExam
                ? format(new Date(assignment.data.dateOfExam), "MMM dd, yyyy HH:mm")
                : "Not set"} (Your timezone: {Intl.DateTimeFormat().resolvedOptions().timeZone})
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="graceTime">Grace Time (minutes)</Label>
            <Input
              id="graceTime"
              type="number"
              min="5"
              max="1440"
              step="5"
              value={formData.graceTime}
              onChange={(e) => handleChange("graceTime", e.target.value)}
              disabled={assignment?.data?.status === 'completed'}
            />
            <p className="text-xs text-gray-500">
              Additional time after exam when it will still be available
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="status">Status</Label>
            <Select
              value={formData.status}
              onValueChange={(value) => handleChange("status", value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="ongoing">Ongoing</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="disqualified">Disqualified</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {formData.status === 'completed' && (
            <div className="space-y-2">
              <Label htmlFor="reviewStatus">Review Status</Label>
              <Select
                value={formData.reviewStatus}
                onValueChange={(value) => handleChange("reviewStatus", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select review status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Under Review</SelectItem>
                  <SelectItem value="reviewed">Reviewed</SelectItem>
                  <SelectItem value="flagged">Flagged</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex items-center space-x-2">
            <Switch
              id="sendUpdateEmail"
              checked={formData.sendUpdateEmail}
              onCheckedChange={(checked) =>
                handleChange("sendUpdateEmail", checked)
              }
            />
            <Label htmlFor="sendUpdateEmail">
              Send update notification email to candidate
            </Label>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={onClose}
              disabled={updateMutation.isLoading}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={updateMutation.isLoading}
            >
              {updateMutation.isLoading ? "Updating..." : "Update Assignment"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditAssignmentModal;